import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock3, XCircle } from 'lucide-react';

/**
 * After A2B/Cardstream hosted checkout, the gateway redirects back to YSR with
 * ?ysr_payment=success|failed&reason=... — show a dismissible modal and strip query params.
 */
export function PaymentReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pay = params.get('ysr_payment');
    if (pay === 'success' || pay === 'failed' || pay === 'pending') {
      setStatus(pay);
      setReason(params.get('reason') || '');
      setOpen(true);
    }
  }, [location.search]);

  const clearParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('ysr_payment');
    params.delete('reason');
    const qs = params.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setStatus(null);
    setReason('');
    clearParams();
  }, [clearParams]);

  if (!status) return null;

  const success = status === 'success';
  const pending = status === 'pending';

  const getFriendlyFailureMessage = () => {
    const normalized = String(reason || '').toLowerCase();
    if (!normalized) {
      return 'Your payment did not complete. You have not been charged. Please try again.';
    }
    if (
      normalized.includes('avs') ||
      normalized.includes('cv2') ||
      normalized.includes('postcode check') ||
      normalized.includes('address check')
    ) {
      return 'Your bank could not verify your billing address or postcode. Please try again and make sure your address and postcode match your card details.';
    }
    if (normalized.includes('declined') || normalized.includes('refused')) {
      return 'Your bank declined this payment. Please try another card or payment method.';
    }
    if (normalized.includes('verification')) {
      return 'Your payment was received and is being confirmed. This usually completes shortly.';
    }
    return 'We could not confirm this payment yet. Please check your request history shortly.';
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="border-gray-700 bg-gray-900 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            {success ? (
              <>
                <CheckCircle2 className="h-6 w-6 text-green-400 shrink-0" />
                Payment successful
              </>
            ) : pending ? (
              <>
                <Clock3 className="h-6 w-6 text-amber-400 shrink-0" />
                Payment received
              </>
            ) : (
              <>
                <XCircle className="h-6 w-6 text-red-400 shrink-0" />
                Payment not completed
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-gray-300 text-left">
            {success
              ? 'Payment Successful. Your song request has been sent to the artist queue.'
              : pending
                ? 'Thanks — your payment has been received. We are confirming it with the payment gateway and your request should appear shortly.'
                : getFriendlyFailureMessage()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            className="w-full bg-purple-600 hover:bg-purple-700 text-white sm:w-auto"
            onClick={handleClose}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
